import {
    Component,
    Input,
    ViewChild,
    Output,
    EventEmitter,
    ElementRef,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatSelectModule } from '@angular/material/select';
import { FiltersService, GeneralService } from '../../core/';
import { map, startWith } from 'rxjs/operators';
import { fromEvent, Observable } from 'rxjs';

/**
 * This component combines sorting filter with select filters.
 * mat-select don't allow to contain several formControl fields,
 * so logic for separating select filter from sorting is on us.
 *
 *
 * Sorting should refresh by clicking and only sorts by one field.
 * Select filter should apply on click and several columns could at the same time.
 *
 * selected: is formControl for mat-select so contains state of multiple select mat-select
 * selectedFilters: contains only select filters
 */

@Component({
    selector: 'pr-drop-select',
    templateUrl: './drop-select.component.html',
    styleUrls: [
        './drop-select.component.scss',
        '../main-table/main-table.component.scss',
    ],
})
export class DropSelectComponent {
    // selected used to control mat-select
    // includes sorting and select filter
    selected = new FormControl();
    filterList: Array<string> = [];
    filterList$: Observable<any>;
    // variable need to store only select filters from filterList
    // because mat-select has one formControl
    selectedFilters: Array<string> = [];
    // variable that indicates if select filter were emited
    isSelectFilterEmitted = true;

    public bigResult = false;

    @Input() placeholder: string;
    @Input() control: FormControl;
    @Input() optLabel: string;
    @Input() list: Array<any>;
    @Input() selectName: string;
    @Input() filterDef: string;

    // this one checks if table is currenty ordered by this select
    // changes if someone change selectedFilters and did not approve
    @Input()
    set isSelected(flag: boolean) {
        if (!flag) {
            this.selected.patchValue([]);
        }
    }
    @ViewChild('select', { static: true }) select;

    @Output()
    change: EventEmitter<any> = new EventEmitter<any>();
    @Output()
    filterChanged: EventEmitter<any> = new EventEmitter<any>();

    @ViewChild('matOptionNormal', { static: true }) normalSort: MatSelectModule;
    @ViewChild('matOptionReverse', { static: true }) reverseSort: MatSelectModule;
    @ViewChild('selectFilterSearch') selectFilterSearch: ElementRef;

    constructor(
        public filtersService: FiltersService,
        private generalService: GeneralService
    ) {}

    // should fetch on openToggle because table can be initialized before filters fetched
    // and Input() wouldn't be handled
    public selectOpenToggle(isOpen: EventEmitter<boolean>): void {
        if (!isOpen) {
            this.selectClose();
        }
        if (!this.filterDef) {
            return;
        }

        this.filterList = this.filtersService.getFiltersByName(this.filterDef);
        if (this.filterList !== undefined) {
            this.filterList = this.mapOrder(
                this.filterList,
                this.selectedFilters
            );
            this.searchFilter();
        }
    }

    private selectClose(): void {
        // emit seleted filter on close in case user forgot to
        // emit change only if indicator (isSelectFilterEmitted) false
        if (!this.isSelectFilterEmitted) {
            this.emitSelFilterOnClick();
        }
    }

    public searchFilter() {
        this.filterList$ = fromEvent(
            this.selectFilterSearch.nativeElement,
            'input'
        ).pipe(
            startWith(this.filterList),
            // debounceTime(100),
            map(() => {
                const searchText = this.selectFilterSearch.nativeElement.value.toLowerCase();

                const results = this.filterList.filter(
                    (filterVal) =>
                        filterVal &&
                        filterVal.toLowerCase().includes(searchText)
                );

                if (results.length > 100) {
                    this.bigResult = true;
                    return [];
                } else {
                    this.bigResult = false;
                    return results;
                }
            })
        );
    }

    public selectSorting(reverse: boolean) {
        if (reverse) {
            this.selected.patchValue(['reverse']);
        } else {
            this.selected.patchValue(['normal']);
        }
        this.selected.patchValue([
            ...this.selected.value,
            ...this.selectedFilters,
        ]);
        this.select.close();
        this.change.emit({ selectedName: this.selectName, reversed: reverse });
    }

    private filterObject(): any {
        return {
            filterName: this.filterDef,
            // deep copy because after first emit
            // child would be changing without emition because of shared memory
            filterValues: this.generalService.deepObjectCopy(
                this.selectedFilters
            ),
        };
    }

    public selectFilter(filterName: string) {
        // validation for already selected: because functions works on click
        const indexFilter = this.selectedFilters.indexOf(filterName);
        if (indexFilter >= 0) {
            this.selectedFilters.splice(indexFilter, 1);
        } else {
            this.selectedFilters.push(filterName);
        }
        // override select, because need to save 'reversed' and 'normal'
        const selectedMock = new Set([
            ...this.selected.value,
            ...this.selectedFilters,
        ]);
        this.selected.patchValue(Array.from(selectedMock));
        // selecting filter changes indicator state
        this.isSelectFilterEmitted = false;
    }

    public emitSelFilterOnClick() {
        this.filterChanged.emit(this.filterObject());
        // emitting non empty select filter changes emition indicator
        this.isSelectFilterEmitted = true;
        this.select.close();
    }

    // clear all select filters
    // clear if select filters exists
    public clearSelFilter() {
        if (!this.selectedFilters.length) {
            return;
        }
        this.selectedFilters = [];
        this.selected.patchValue([
            ...this.selected.value.filter(
                (val) => val === 'reverse' || val === 'normal'
            ),
        ]);
        this.emitSelFilterOnClick();
    }

    // func orders select filter(asc)
    // with selected filters on top
    private mapOrder(array, order): Array<string> {
        return array
            .sort()
            .reverse()
            .filter((el) => order.includes(el) <= 0)
            .concat(order)
            .reverse();
    }
}
